import React, { ReactNode } from "react"
import { Box, Flex, Text } from "@chakra-ui/react"
import Button from "components/Buttons/Button"

export const PORTAL_COLUMN_WIDTH = 710
export const SECTION_LABEL_WIDTH = 164
export const SECTION_EDIT_WIDTH = 86

// Preview only: no clicks, no keyboard focus (React 18 takes `inert` as a string).
const nonInteractive = { inert: "", pointerEvents: "none" as const }

type Props = {
  children: ReactNode
  showSectionOverlays?: boolean
  handleClick?: () => void
  sectionName?: string
  isFirstBlock?: boolean
  isLastBlock?: boolean
  background?: string
}

const PreviewBlock = ({
  children,
  showSectionOverlays,
  handleClick,
  sectionName,
  isFirstBlock,
  isLastBlock,
  background,
}: Props) => {
  const childrenWrapperBoxConfig = {
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomRadius: isLastBlock ? "lg" : 0,
    borderTopRadius: isFirstBlock ? "lg" : 0,
    borderBottomWidth: isLastBlock ? 1 : 0,
    overflow: "hidden",
    w: "full",
    maxW: `${PORTAL_COLUMN_WIDTH}px`,
    ...nonInteractive,
  }

  if (!showSectionOverlays) {
    return (
      <Box {...childrenWrapperBoxConfig} margin="auto" background={background}>
        {children}
      </Box>
    )
  }

  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      position="relative"
      w="full"
      sx={{
        "&::after": {
          content: "\"\"",
          position: "absolute",
          left: 0,
          bottom: 0,
          width: "100%",
          borderBottom: sectionName && handleClick ? "1px dashed" : "none",
          borderColor: "gray.300",
        },
      }}
    >
      <Flex w={`${SECTION_LABEL_WIDTH}px`} flexShrink={0} justifyContent="flex-start" pr={6}>
        {sectionName && (
          <Text color="blue.400">
            {sectionName}
          </Text>
        )}
      </Flex>
      <Box
        flex={1}
        minW={0}
        alignSelf="stretch"
        background={background}
        minH={handleClick ? 8 : 0}
        sx={{ containerType: "inline-size" }}
      >
        <Box maxW={`${PORTAL_COLUMN_WIDTH}px`} mx="auto" {...nonInteractive}>
          {children}
        </Box>
      </Box>
      <Flex w={`${SECTION_EDIT_WIDTH}px`} flexShrink={0} justifyContent="flex-end" pl={6}>
        {handleClick && (
          <Button size="xs" variant="outline" onClick={handleClick}>
            Edit
          </Button>
        )}
      </Flex>
    </Flex>
  )
}

export default PreviewBlock
